// =============================================================
//  LoginScreen — Connexion (refonte split-screen dashboard)
//  Logique d'auth Firebase préservée à 100%.
// =============================================================

import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Platform, Alert } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { auth, db } from '../private/firebaseConfig';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useAuth } from '../private/AuthContext';
import { doc, getDoc } from 'firebase/firestore';

import AuthLayout from './auth-ui/AuthLayout';
import { Input, Button } from '../admin/web-ui/kit';
import { colors, spacing } from '../admin/web-ui/theme';

export default function LoginScreen({ navigation }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState(null);
  const { userType } = useAuth();

  // Redirection automatique si déjà connecté
  useEffect(() => {
    if (userType) {
      switch (userType) {
        case 'admin':
          navigation.replace('TabNavigatorAdmin');
          break;
        case 'user':
          navigation.replace('TabNavigatorUser');
          break;
        case 'center':
          navigation.replace('TabNavigatorCentre');
          break;
        default:
          navigation.replace('TabNavigatorUser');
      }
    }
  }, [userType, navigation]);

  const showAlert = (title, message, onPress) => {
    if (Platform.OS === 'web') {
      window.alert(`${title}\n\n${message}`);
      if (onPress) onPress();
    } else {
      Alert.alert(title, message, [{ text: 'OK', onPress }]);
    }
  };

  const getErrorMessage = (code) => {
    switch (code) {
      case 'auth/invalid-email': return 'Adresse email invalide';
      case 'auth/user-disabled': return 'Ce compte a été désactivé';
      case 'auth/user-not-found': return 'Aucun compte trouvé avec cet email';
      case 'auth/wrong-password': return 'Mot de passe incorrect';
      case 'auth/invalid-credential': return 'Identifiants incorrects';
      case 'auth/too-many-requests': return 'Trop de tentatives. Réessaie plus tard.';
      case 'auth/network-request-failed': return 'Erreur de connexion réseau';
      default: return 'Une erreur est survenue';
    }
  };

  const validateFields = () => {
    const e = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) e.email = "L'email est requis";
    else if (!emailRegex.test(email.trim())) e.email = "Format d'email invalide";
    if (!password) e.password = 'Le mot de passe est requis';
    else if (password.length < 6) e.password = 'Au moins 6 caractères';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleLogin = async () => {
    setGlobalError(null);
    if (!validateFields()) return;
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
      const uid = userCredential.user.uid;

      const adminDoc = await getDoc(doc(db, 'admin', uid));
      if (adminDoc.exists()) {
        // Connexion réussie pour un admin — la redirection est gérée par useEffect via userType.
        return;
      }
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) return;

      const centerDoc = await getDoc(doc(db, 'centers', uid));
      if (centerDoc.exists()) return;

      throw new Error('user-type-not-found');
    } catch (error) {
      console.error('Login error:', error);
      let msg = getErrorMessage(error.code);
      if (error.message === 'user-type-not-found') {
        msg = 'Type de compte non reconnu. Vérifie tes identifiants.';
      }
      setGlobalError(msg);
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Bon retour 👋"
      subtitle="Connecte-toi pour accéder à ton espace MyConcours."
      footer={
        <View style={styles.footerRow}>
          <Text style={styles.footerText}>Tu n'as pas encore de compte ?</Text>
          <TouchableOpacity onPress={() => navigation.navigate('RegisterScreen')}>
            <Text style={styles.footerLink}>S'inscrire</Text>
          </TouchableOpacity>
        </View>
      }
    >
      {!!globalError && (
        <View style={styles.errorBox}>
          <MaterialCommunityIcons name="alert-circle-outline" size={16} color={colors.danger} />
          <Text style={styles.errorBoxText}>{globalError}</Text>
        </View>
      )}

      <Field label="Email" error={errors.email}>
        <Input
          icon="email-outline"
          value={email}
          onChangeText={setEmail}
          placeholder="ton.email@exemple.com"
          type="email"
          autoCapitalize="none"
          autoComplete="email"
          autoCorrect={false}
        />
      </Field>

      <Field label="Mot de passe" error={errors.password}>
        <Input
          icon="lock-outline"
          value={password}
          onChangeText={setPassword}
          placeholder="••••••••"
          type={showPassword ? 'text' : 'password'}
          rightIcon={showPassword ? 'eye-off-outline' : 'eye-outline'}
          onRightIconPress={() => setShowPassword((v) => !v)}
          autoCapitalize="none"
        />
      </Field>

      <TouchableOpacity
        style={styles.forgotBtn}
        onPress={() => navigation.navigate('ForgotPasswordScreen')}
      >
        <Text style={styles.forgotText}>Mot de passe oublié ?</Text>
      </TouchableOpacity>

      <Button
        icon="login"
        onPress={handleLogin}
        loading={loading}
        fullWidth
        style={{ marginTop: 8 }}
      >
        Se connecter
      </Button>
    </AuthLayout>
  );
}

function Field({ label, error, children }) {
  return (
    <View style={{ marginBottom: spacing.md }}>
      <Text style={styles.label}>{label}</Text>
      {children}
      {!!error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
}

const styles = StyleSheet.create({
  label: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 6,
  },
  errorText: {
    fontSize: 12,
    color: colors.danger,
    marginTop: 4,
  },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.dangerSoft,
    borderWidth: 1,
    borderColor: colors.danger + '40',
    borderRadius: 10,
    padding: 12,
    marginBottom: spacing.md,
  },
  errorBoxText: {
    flex: 1,
    fontSize: 13,
    color: colors.danger,
    fontWeight: '600',
  },
  forgotBtn: {
    alignSelf: 'flex-end',
    marginTop: -4,
    marginBottom: 16,
    padding: 4,
  },
  forgotText: {
    color: colors.primary,
    fontSize: 13,
    fontWeight: '600',
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  footerText: { color: colors.textSecondary, fontSize: 13 },
  footerLink: { color: colors.primary, fontSize: 13, fontWeight: '700' },
});
